// User profile control (2026-09-09 founder request: "where to sign out and
// then sign in?"). Anchored menu on the existing `dropdown-menu` primitive:
// a 44px avatar trigger, a non-interactive name/email header sourced from
// `useCurrentUser` (never the Auth0 `sub`), then Settings and Sign out.
//
// Sign out: Auth0 `logout({ logoutParams: { returnTo: origin } })`, plus
// clearing the user-scoped job-id sessionStorage (jobRegistry's
// `clearTrackedJobs`, §5 "clear on logout"). The React Query cache is
// already cleared by `ProtectedRoute`'s `AuthenticatedSession` cleanup when
// the authenticated session unmounts (its `getAccessTokenSilently` provider
// is revoked and `queryClient.clear()` runs there) -- clearing it again here
// would require a `QueryClientProvider` this menu doesn't otherwise depend
// on. In mock-auth mode there is no Auth0 session to end, so sign-out
// degrades to a local clear + route to `/login` rather than calling into a
// provider that was never mounted.
import { useAuth0 } from '@auth0/auth0-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { sitevars } from '@/config/sitevars';
import { isAuth0Configured } from '@/auth/Auth0ProviderWithNavigate';
import { useLocalPasswordSession } from '@/auth/localPasswordSession';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@moneyskyline/ui';
import { useCurrentUser } from '@/api/hooks/useCurrentUser';
import { clearTrackedJobs } from '@/features/transactions/tools/jobRegistry';
import { scopeHref } from './scope';

const MOCK_AUTH_ENABLED = import.meta.env.DEV && import.meta.env.VITE_MOCK_AUTH === 'true';

function Avatar({ picture, initials }: { picture: string | null; initials: string }) {
  if (picture) {
    return (
      <img
        src={picture}
        alt=""
        referrerPolicy="no-referrer"
        className="h-7 w-7 shrink-0 rounded-full object-cover"
      />
    );
  }
  return (
    <span
      aria-hidden="true"
      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold text-muted-foreground"
    >
      {initials}
    </span>
  );
}

function AccountMenuView({ showLabel = false, onSignOut }: { showLabel?: boolean; onSignOut: () => void }) {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useCurrentUser();

  const displayName = user?.name ?? 'Account';
  const displayEmail = user?.email ?? null;
  const initials = user?.initials ?? '?';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Account menu"
          data-testid="account-menu-trigger"
          className="ms-tap-44 flex min-h-11 items-center gap-2 rounded-md px-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Avatar picture={user?.picture ?? null} initials={initials} />
          {showLabel && <span className="max-w-[10rem] truncate">{displayName}</span>}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-56">
        <DropdownMenuLabel className="flex flex-col gap-0.5 py-2">
          <span className="truncate text-sm font-semibold text-foreground" data-testid="account-menu-name">
            {displayName}
          </span>
          {displayEmail && (
            <span className="truncate text-xs font-normal text-muted-foreground" data-testid="account-menu-email">
              {displayEmail}
            </span>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="min-h-11"
          data-testid="account-menu-settings"
          onSelect={() => navigate(scopeHref('/settings', location.search))}
        >
          Settings
        </DropdownMenuItem>
        <DropdownMenuItem
          className="min-h-11"
          data-testid="account-menu-sign-out"
          onSelect={onSignOut}
        >
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function Auth0AccountMenu({ showLabel = false }: { showLabel?: boolean }) {
  const { logout } = useAuth0();
  return (
    <AccountMenuView
      showLabel={showLabel}
      onSignOut={() => {
        clearTrackedJobs();
        logout({ logoutParams: { returnTo: window.location.origin } });
      }}
    />
  );
}

function LocalPasswordAccountMenu({ showLabel = false }: { showLabel?: boolean }) {
  const navigate = useNavigate();
  const localSession = useLocalPasswordSession();
  return (
    <AccountMenuView
      showLabel={showLabel}
      onSignOut={() => {
        clearTrackedJobs();
        localSession?.setToken(null);
        navigate('/login', { replace: true });
      }}
    />
  );
}

function JointLocalAndAuth0AccountMenu({ showLabel = false }: { showLabel?: boolean }) {
  const { logout } = useAuth0();
  const localSession = useLocalPasswordSession();
  return (
    <AccountMenuView
      showLabel={showLabel}
      onSignOut={() => {
        clearTrackedJobs();
        localSession?.setToken(null);
        logout({ logoutParams: { returnTo: window.location.origin } });
      }}
    />
  );
}

export function AccountMenu({ showLabel = false }: { showLabel?: boolean }) {
  if (MOCK_AUTH_ENABLED) {
    return <LocalPasswordAccountMenu showLabel={showLabel} />;
  }
  if (sitevars.localPasswordAuth && isAuth0Configured()) {
    return <JointLocalAndAuth0AccountMenu showLabel={showLabel} />;
  }
  if (sitevars.localPasswordAuth) {
    return <LocalPasswordAccountMenu showLabel={showLabel} />;
  }
  return <Auth0AccountMenu showLabel={showLabel} />;
}
